import { PillTones } from '@clockout-dev/design-elements';
import {
    ArrowsRightLeftIcon,
    DocumentDuplicateIcon,
    LockClosedIcon,
    LockOpenIcon,
    UserIcon,
} from '@heroicons/react/24/outline';
import { CONNECTION_STATES } from '../../common/payrollConnections/payrollConnections.constants';

/**
 * How the header's enrollment chip renders (KAN-738). The first three are the
 * product variants; ENROLLED_ONLY is a member with an enrollment date and no
 * opt-in event (their first sign-in was not tagged `new_account`), so only the
 * date we have is shown. LOADING and UNAVAILABLE are the two non-answers, and
 * they must never read as NOT_COMPLETED. HIDDEN covers "the payload does not carry
 * the dates yet" (pre-KAN-784) and "member never opted in".
 */
export const ENROLLMENT_VARIANTS = Object.freeze({
    SAME_DAY: 'same_day',
    DIFFERENT_DAYS: 'different_days',
    NOT_COMPLETED: 'not_completed',
    ENROLLED_ONLY: 'enrolled_only',
    LOADING: 'loading',
    UNAVAILABLE: 'unavailable',
    HIDDEN: 'hidden',
});

/**
 * Where the enrollment dates are in their lifecycle, as the controller hands
 * them to the header. `PENDING` = the consumer payload has no date fields at
 * all, i.e. KAN-784 is not deployed yet.
 */
export const ENROLLMENT_STATUS = Object.freeze({
    PENDING: 'pending',
    LOADING: 'loading',
    ERROR: 'error',
    READY: 'ready',
});

export const ENROLLMENT_DATE_FORMAT = 'MMM D, YYYY';

export const ENROLLMENT_COPY = Object.freeze({
    OPTED_IN_AND_ENROLLED: 'Opted in and enrolled',
    OPTED_IN: 'Opted in',
    ENROLLED: 'Enrolled',
    NOT_COMPLETED: 'Enrollment not completed',
    UNAVAILABLE: 'Enrollment dates unavailable',
    UNAVAILABLE_TOOLTIP:
        "We couldn't load this member's opt-in and enrollment dates. The rest of the page is unaffected.",
    LOADING_LABEL: 'Loading enrollment dates',
});

export const PAY_TYPES = Object.freeze({
    SALARIED: 'salaried',
    HOURLY: 'hourly',
});

export const PAY_TYPE_LABELS = Object.freeze({
    [PAY_TYPES.SALARIED]: 'Salaried',
    [PAY_TYPES.HOURLY]: 'Hourly',
});

/**
 * Pay cadence tokens, as the payroll-connections endpoint sends them
 * (`pay_frequency`). Not derivable from `payPeriod`, which is a date range,
 * not a frequency. `variable` is deliberately absent: it means "no settled
 * cadence", so the chip shows the pay type alone.
 */
export const PAY_FREQUENCY_LABELS = Object.freeze({
    daily: 'Daily',
    weekly: 'Weekly',
    'bi-weekly': 'Every 2 weeks',
    'semi-monthly': 'Twice a month',
    monthly: 'Monthly',
});

export const PAYROLL_SOURCE_STATUS = Object.freeze({
    UP_TO_DATE: 'up_to_date',
    NOT_UPDATING: 'not_updating',
    SYNCING: 'syncing',
});

export const PAYROLL_SOURCE_STATUS_TONES = Object.freeze({
    [PAYROLL_SOURCE_STATUS.UP_TO_DATE]: PillTones.SUCCESS,
    [PAYROLL_SOURCE_STATUS.NOT_UPDATING]: PillTones.WARNING,
    [PAYROLL_SOURCE_STATUS.SYNCING]: PillTones.NEUTRAL,
});

/**
 * A source's badge from its connection `state` (payroll-connections, derived
 * by clockout-service `lib/sync-status.js#connectionStateFor`) — the interim
 * rule until product names the `access_state` behind "Refresh & Advance".
 * "Up to date" means the connection's last sync completed, not that the
 * payroll data is recent. A state not listed here renders no badge.
 */
export const CONNECTION_STATE_SOURCE_STATUS = Object.freeze({
    [CONNECTION_STATES.CONNECTED]: PAYROLL_SOURCE_STATUS.UP_TO_DATE,
    [CONNECTION_STATES.FAILED]: PAYROLL_SOURCE_STATUS.NOT_UPDATING,
    [CONNECTION_STATES.NOT_CONNECTED]: PAYROLL_SOURCE_STATUS.NOT_UPDATING,
    [CONNECTION_STATES.UNRESOLVED]: PAYROLL_SOURCE_STATUS.NOT_UPDATING,
    [CONNECTION_STATES.IN_PROGRESS]: PAYROLL_SOURCE_STATUS.SYNCING,
});

/** Figma «Employer popover» width. */
export const PAYROLL_SOURCES_POPOVER_WIDTH = 420;

export const PAYROLL_SOURCE_STATUS_LABELS = Object.freeze({
    [PAYROLL_SOURCE_STATUS.UP_TO_DATE]: 'Up to date',
    [PAYROLL_SOURCE_STATUS.NOT_UPDATING]: 'Not updating',
    [PAYROLL_SOURCE_STATUS.SYNCING]: 'Syncing',
});

/** Leading icon of the page's breadcrumb trail in the app header (users section). */
export const BREADCRUMB_ICON = UserIcon;

export const USER_HEADER_COPY = Object.freeze({
    UNKNOWN_NAME: 'Unknown user',
    BREADCRUMB_SECTION: 'Reports',
    BREADCRUMB_ICON_LABEL: 'Users',
    USER_ID_PREFIX: 'ID',
    USER_ID_LABEL: 'user ID',
    USER_ID_COPIED: 'User ID copied',
    USER_ID_COPY_FAILED: 'Could not copy the user ID',
    PAYROLL_SOURCES_SINGULAR: 'payroll source',
    PAYROLL_SOURCES_PLURAL: 'payroll sources',
    PAYROLL_SOURCES_TITLE_SINGULAR: 'Payroll source',
    PAYROLL_SOURCES_TITLE_PLURAL: 'Payroll sources',
    MORE_ACTIONS: 'More actions',
    VIEW_AS_USER: 'View as user',
});

export const MORE_ACTION_KEYS = Object.freeze({
    COPY_USER_ID: 'copy-user-id',
    DIVIDER: 'divider',
    RECONCILE: 'reconcile',
    RESTRICT: 'restrict',
});

/**
 * What a More actions entry is, as the controller describes it. The view maps
 * each type to its icon (MORE_ACTION_ICONS); DIVIDER is the rule between the
 * copy action and the gated ones.
 */
export const MORE_ACTION_TYPES = Object.freeze({
    COPY_USER_ID: 'copy_user_id',
    RECONCILE: 'reconcile',
    RESTRICT: 'restrict',
    LIFT_RESTRICTION: 'lift_restriction',
    DIVIDER: 'divider',
});

/** Figma «More actions» icons (copy-01, coins-swap-01, lock-01, lock-unlocked). */
export const MORE_ACTION_ICONS = Object.freeze({
    [MORE_ACTION_TYPES.COPY_USER_ID]: DocumentDuplicateIcon,
    [MORE_ACTION_TYPES.RECONCILE]: ArrowsRightLeftIcon,
    [MORE_ACTION_TYPES.RESTRICT]: LockClosedIcon,
    [MORE_ACTION_TYPES.LIFT_RESTRICTION]: LockOpenIcon,
});

export const MORE_ACTION_COPY = Object.freeze({
    COPY_USER_ID: 'Copy user ID',
    RECONCILE: 'Reconcile account',
    /** @param {string} amount formatted, e.g. "$31.99" */
    RECONCILE_OWED: (amount) => `Clear ${amount} owed.`,
    RECONCILE_DESCRIPTION: 'Settle outstanding debt with one ledger adjustment.',
    RESTRICT: 'Restrict access',
    RESTRICT_DESCRIPTION: 'Stops new advances. You can undo it.',
    LIFT_RESTRICTION: 'Lift restriction',
    LIFT_RESTRICTION_DESCRIPTION: 'Lets the member request advances again.',
});

/**
 * User ID (the member's `client_id`) display in the meta line: a long id
 * is shortened to `c398…ed15f2` (Figma); copying always uses the full id.
 */
export const USER_ID_HEAD = 4;
export const USER_ID_TAIL = 6;
